import Vue from 'vue';
import { getAccessToken } from './auth';

// Memory only: never share editing identities through storage or across tabs.
const sessions = {};
export const editSessionState = new Vue({ data: { revision: 0 } });
let transport;
const changed = () => { editSessionState.revision++; };
const fail = msg => ({ msg, originalMsg: msg });
const terminal = e => /^(PROJECT_EDIT_SESSION_REQUIRED|PROJECT_LOCKED|PROJECT_LOCK_EXPIRED|PROJECT_LOCK_NOT_OWNED)$/.test(e.originalMsg || '');
export function getProjectSession(id) { return sessions[String(id)]; }
function clear(s) {
    if (getProjectSession(s.projectId) !== s) return;
    clearInterval(s.timer);
    delete sessions[String(s.projectId)];
    changed();
}
function invalidate(s, e) {
    s.status = terminal(e) ? 'lost' : 'paused';
    if (s.status === 'lost') { clearInterval(s.timer); if(s.importing) { clear(s); window.dispatchEvent(new Event('project-import-finished')); } }
    changed();
}
// Backend Jackson uses SNAKE_CASE for DTOs; Java field names are not JSON keys.
function lease(s, action) {
    return transport({ url: '/project/' + action, method: 'POST', loading: false, timeout: 15000,
        data: { project_id: s.projectId, edit_session_id: s.id } });
}
export async function confirmProjectSession(id) {
    const s = getProjectSession(id);
    if (!s || s.status === 'lost') throw fail('PROJECT_LOCK_EXPIRED');
    if (s.check) return s.check;
    s.status = 'paused'; changed();
    s.check = lease(s, 'heartbeat').then(() => { if(s.status !== 'lost') { s.status = 'active'; s.expiresAt = Date.now() + 600000; changed(); } })
        .catch(e => { invalidate(s, e); throw e; }).then(v => { s.check = null; return v; }, e => { s.check = null; throw e; });
    return s.check;
}
export async function acquireProjectSession(projectId) {
    let s = getProjectSession(projectId);
    if (s && s.closing) throw fail('编辑会话正在结束');
    if (s && s.acquiring) return s.acquiring;
    if (s && s.status === 'active' && Date.now() < s.expiresAt) return s;
    if(s && s.status === 'active') s.status = 'paused';
    if (s && s.status === 'paused' && s.acquired) { await confirmProjectSession(projectId); return s; }
    if (!s || s.status === 'lost') {
        if (!window.crypto || !window.crypto.randomUUID) throw fail('浏览器不支持安全编辑会话，请使用 HTTPS 或 localhost');
        s = { projectId, id: window.crypto.randomUUID(), status: 'acquiring', pending: [], closing: false };
        sessions[String(projectId)] = s; changed();
    }
    s.acquiring = (async () => {
        try {
            // A timeout retry must carry exactly the same UUID.
            try { await lease(s, 'lock'); } catch (e) {
                if (e.type !== 'NETWORK_ERROR') throw e;
                await lease(s, 'lock');
            }
            s.acquired = true; s.status = 'active'; s.expiresAt = Date.now() + 600000;
            s.timer = setInterval(() => { confirmProjectSession(projectId).catch(() => {}); }, 60000);
            changed(); return s;
        } catch (e) { invalidate(s, e); throw e; }
    })();
    try { return await s.acquiring; } finally { s.acquiring = null; }
}
export async function waitProjectWrites(id) {
    const s = getProjectSession(id);
    if(s) await Promise.all(s.pending.map(p => p.catch(() => {})));
}
export async function releaseProjectSession(id) {
    const s = getProjectSession(id);
    if (!s) return;
    if (s.release) return s.release;
    s.closing = true; changed();
    s.release = (async () => {
        await Promise.all(s.pending.map(p => p.catch(() => {})));
        if (s.acquiring) await s.acquiring.catch(() => {});
        if (s.check) await s.check.catch(() => {});
        if (s.status !== 'lost' && getAccessToken()) {
            for (let i = 0; i < 2; i++) {
                try { await lease(s, 'unlock'); break; } catch (e) {
                    if (terminal(e)) break;
                }
            }
        }
        clear(s);
    })();
    return s.release;
}
const writes = /^(?:\/?project)\/(save|sub-item\/batch-update|sheet\/create|sheet\/delete|sheet\/rename|save-field-cost|switch-mode|edit|archive|set-won-bid|delete|import)(?:\?|$)/;
export async function projectWrite(options, send) {
    const match = options.url.match(writes);
    if (!match) return send(options);
    const action = match[1];
    const data = options.data;
    const id = data instanceof FormData ? data.get('project_id') : data.projectId || data.project_id;
    const management = ['archive', 'set-won-bid', 'delete', 'import'].indexOf(action) !== -1;
    const existed = getProjectSession(id);
    const s = management ? await acquireProjectSession(id) : existed;
    if(s && s.status === 'active' && Date.now() >= s.expiresAt) { s.status = 'paused'; changed(); }
    if (!s || s.status !== 'active' || s.closing) throw fail('编辑权尚未确认，已暂停提交；请确认会话后重试');
    if (s.importing) throw fail('导入正在后台处理，请等待系统消息');
    if (management) {
        s.closing = true; changed();
        await Promise.all(s.pending.map(p => p.catch(() => {})));
        try {
            await confirmProjectSession(id);
            if(action === 'import') {
                const messages = await transport({url:'/msg/search', method:'POST', loading:false, data:{page:1, limit:1, msg_status:-1}});
                s.importAfter = Math.max(0, ...(messages.records || []).map(row => Number(row.msg_id)));
            }
        } catch(e) { s.closing = false; changed(); if(!existed) await releaseProjectSession(id); throw e; }
    }
    options.headers = { ...options.headers, 'X-Project-Edit-Session': s.id };
    const p = send(options);
    s.pending.push(p); changed();
    try {
        const result = await p;
        if (['archive', 'set-won-bid', 'delete'].indexOf(action) !== -1) clear(s);
        if (action === 'import') s.importing = true;
        return result;
    } catch (e) {
        if (terminal(e) || e.type === 'NETWORK_ERROR' || e.originalMsg === 'PROJECT_LOCK_SERVICE_UNAVAILABLE') invalidate(s, e);
        // An upload timeout can still have submitted a task. Keep renewing, never resend it automatically.
        if (action === 'import' && e.type === 'NETWORK_ERROR') s.importing = true;
        throw e;
    } finally {
        s.pending = s.pending.filter(item => item !== p);
        s.closing = false; changed();
        if (management && !existed && !s.importing && getProjectSession(id) === s) await releaseProjectSession(id);
    }
}
export function bestEffortUnlock(id) {
    const s = getProjectSession(id);
    if (!s || s.status === 'lost' || s.importing || s.pending.length) return;
    s.status = 'paused'; changed();
    fetch(String(process.env.API_ROOT || '').replace(/\/$/, '') + '/project/unlock', {
        method: 'POST', credentials: 'include', keepalive: true,
        headers: { Authorization: getAccessToken(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: s.projectId, edit_session_id: s.id })
    }).catch(() => {});
}
export function configureProjectSessions(send) {
    transport = send;
    const resume = () => Object.keys(sessions).forEach(id => {
        const s = sessions[id];
        if (s.acquired && s.status !== 'lost') confirmProjectSession(id).catch(() => {});
    });
    window.addEventListener('online', resume);
    window.addEventListener('pageshow', resume);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) resume(); });
    window.addEventListener('offline', () => Object.keys(sessions).forEach(id => { if(sessions[id].status !== 'lost') sessions[id].status = 'paused'; changed(); }));
    window.addEventListener('pagehide', () => Object.keys(sessions).forEach(bestEffortUnlock));
}
// Reuse the existing system message feed; do not invent an import-result endpoint.
let checkingImports = false;
export async function checkImportResults() {
    const pending = Object.keys(sessions).map(id => sessions[id]).filter(s => s.importing);
    if(!pending.length || checkingImports) return;
    checkingImports = true;
    try {
        const oldest = Math.min(...pending.map(s => s.importAfter || 0));
        let page = 1;
        while(true) {
            const result = await transport({url:'/msg/search', method:'POST', loading:false, data:{page, limit:100, msg_status:-1}});
            const records = result.records || [];
            for(const s of pending) {
                if(getProjectSession(s.projectId) !== s) continue;
                const message = records.find(row => Number(row.msg_id) > s.importAfter &&
                    /^项目原始数据导入(成功|失败)$/.test(row.msg_title) &&
                    String(row.msg_detail).indexOf('项目 [' + s.projectName + ']') !== -1);
                if(message) {
                    if(message.msg_title === '项目原始数据导入成功') {
                        // Names in legacy messages may repeat; never release a still-valid import lease on a name match alone.
                        await confirmProjectSession(s.projectId).catch(e => { if(!terminal(e)) throw e; });
                    }
                    else { s.importing = false; await releaseProjectSession(s.projectId); }
                    window.dispatchEvent(new Event('project-import-finished'));
                }
            }
            if(!records.length || records.some(row => Number(row.msg_id) <= oldest) || page * 100 >= result.total) break;
            page++;
        }
    } finally { checkingImports = false; }
}



// WEBPACK FOOTER //
// ./src/utils/project-edit-session.js